import { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../auth/auth-context';
import { AuthShell } from '../components/auth-shell';
import { usePageTitle } from '../lib/page-title';

/**
 * Landing route for Supabase email links (confirmation, recovery). The client
 * exchanges the PKCE code automatically on load; this page only routes the
 * resulting state: recovery goes to the new-password form, a plain session to
 * the account, and errors are shown with a way back.
 */
export function AuthCallbackPage() {
  usePageTitle('Signing in');
  const { session, initializing, recovering } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const errorDescription = searchParams.get('error_description') ?? searchParams.get('error');

  useEffect(() => {
    if (initializing || errorDescription) {
      return;
    }
    if (recovering) {
      void navigate('/reset/confirm', { replace: true });
    } else if (session) {
      void navigate('/account', { replace: true });
    }
  }, [initializing, recovering, session, errorDescription, navigate]);

  if (errorDescription) {
    return (
      <AuthShell>
        <h1>That link did not work</h1>
        <p role="alert">{errorDescription}</p>
        <Link className="text-link" to="/signin">
          Back to sign in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <p role="status">Completing sign-in…</p>
      {!initializing && !session ? (
        <p>
          Taking too long?{' '}
          <Link className="text-link" to="/signin">
            Return to sign in
          </Link>
        </p>
      ) : null}
    </AuthShell>
  );
}
